// Host ledger: the Network figure's Hosts table (DESIGN_SPEC §11.4.8, §10.9 rugs; RESTYLE §2 threat tags).
//   <HostLedger rows axis live? now? />
//     host · address (name, then addresses and ports; a host that is not OS background and was reached by the sample
//     subtree, or by nobody the guest named, carries ThreatTags for its telling facts: upload, beacon cadence, odd port,
//     no DNS name, plaintext HTTP, tinted by the most severe tag citing it, info when none) · process (name and pid, or
//     the host capture / TLS proxy with no pid) · requests (the busiest method and path ×N, its status and cadence) ·
//     sent · received · first seen · contacts on the run clock (a rug; live: up to a now line)
//   <Rug contacts axis now? background? />   one dot per contact; the axis is { from, to } in ms from T0
import type { CSSProperties } from "react";
import { ThreatTag } from "@/components/ui";
import { bytes, clock, count, dur, plural, rel } from "@/lib/format";
import { cadenceText } from "../derive/fold";
import { hostSignals, type HostLedgerRow } from "../derive/hosts";
import { SigBadges } from "./NetTables";

export type Axis = { from: number; to: number };

const css = (vars: Record<string, number>) => vars as CSSProperties;

/** The rug's span for the column head: "to +25.9 s" (from T0), "to 5:00" while live (the timeout). */
function axisText(axis: Axis, live?: boolean): string {
  return live ? `to ${clock(axis.to)}` : `to +${dur(axis.to)}`;
}

function Rug({ contacts, axis, now, background }: { contacts: readonly number[]; axis: Axis; now?: number; background?: boolean }) {
  const span = Math.max(1, axis.to - axis.from);
  const pct = (t: number) => Math.min(100, Math.max(0, ((t - axis.from) / span) * 100));
  const xs = [...new Set(contacts.map((t) => Math.round(pct(t) * 4) / 4))];
  const label = contacts.length
    ? `${count(contacts.length)} ${plural(contacts.length, "contact")} from ${rel(contacts[0], 1)} to ${rel(contacts[contacts.length - 1], 1)}`
    : "no contacts";
  return (
    <div className="px-1">
      <div className={`rug ${background ? "[&>i]:bg-ink-faint" : ""}`} role="img" aria-label={label}>
        {xs.map((x) => (
          <i key={x} style={css({ "--x": x })} />
        ))}
        {now !== undefined && <span className="now" style={css({ "--x": pct(now) })} />}
      </div>
    </div>
  );
}

function addrLine(row: HostLedgerRow): string {
  const addrs = row.host ? row.addrs : row.addrs.slice(1);
  const shown = addrs.slice(0, 3).join(" ");
  const more = addrs.length > 3 ? ` +${addrs.length - 3}` : "";
  const ports = row.ports.map((p) => `:${p}`).join(" ");
  return [row.host ? `${shown}${more}` : "no DNS name", ports].filter(Boolean).join(" ");
}

function requestCells(row: HostLedgerRow): [string, string] {
  const top = row.requests[0];
  if (top) {
    const more = row.requests.length > 1 ? ` +${row.requests.length - 1}` : "";
    const cad = top.cadenceSec ?? (row.requests.length === 1 ? undefined : row.cadenceSec);
    const second = [top.status !== undefined ? String(top.status) : "", cad ? cadenceText(cad) : top.scheme === "http" ? "plaintext" : ""].filter(Boolean).join(" · ");
    return [`${top.method} ${top.path} ×${count(top.count)}${more}`, second || "no status"];
  }
  if (row.connCount) return ["tcp only", [`${count(row.connCount)} ${plural(row.connCount, "connection")}`, row.cadenceSec ? cadenceText(row.cadenceSec) : ""].filter(Boolean).join(" · ")];
  return ["capture only", `${count(row.flowCount)} ${plural(row.flowCount, "flow")}`];
}

export function HostLedger({ rows, axis, live, now, onInspect }: { rows: readonly HostLedgerRow[]; axis: Axis; live?: boolean; now?: number; onInspect?: (ip: string, port?: number) => void }) {
  return (
    <div className="tscroll relative">
      <table className="t t--lg">
        <thead>
          <tr>
            <th>host · address</th>
            <th>process</th>
            <th>requests</th>
            <th className="r" title="Sent ↑ and received ↓">
              bytes
            </th>
            <th className="r" title="First contact, from T0">
              first
            </th>
            <th className="w-full min-w-[120px] whitespace-normal">contacts · {axisText(axis, live)}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const tags = live ? [] : hostSignals(row).slice(0, 2);
            const [req1, req2] = requestCells(row);
            const proc = row.process;
            const others = row.procs.length - 1;
            const cited = row.citeTags.length ? `Cited by ${row.citeTags.join(", ")}` : "No behavior tag cites this host";
            return (
              <tr key={row.key} className={row.background ? "[&_td]:text-ink-mute" : undefined}>
                <td className="min-w-[33ch] whitespace-normal py-2 align-top max-[720px]:min-w-[26ch]">
                  <div className={`[overflow-wrap:anywhere] ${row.background ? "" : "primary"}`} translate="no">
                    {row.host ?? row.addrs[0]}
                  </div>
                  <div className="faint [overflow-wrap:anywhere]" translate="no">
                    {addrLine(row)}
                    {row.background && row.service && <> · background · {row.service}</>}
                  </div>
                  {tags.length > 0 && (
                    <ul className="mt-1.5 flex flex-wrap gap-1" aria-label="What stands out">
                      {tags.map((t) => (
                        <li key={t.kind} className="flex">
                          <ThreatTag sev={row.citeSev} title={`${t.label}. ${cited}.`} className="h-5 text-[10.5px]">
                            {t.label}
                          </ThreatTag>
                        </li>
                      ))}
                    </ul>
                  )}
                  <SigBadges
                    sigs={row.sigs}
                    limit={4}
                    onInspect={
                      onInspect && row.addrs.length
                        ? () => onInspect(row.addrs[0], row.ports[0])
                        : undefined
                    }
                  />
                </td>
                <td className="py-2 align-top">
                  {proc ? (
                    <>
                      <div className="text-ink" translate="no">
                        {proc.name || `pid ${proc.pid}`}
                      </div>
                      <div className="faint tabular-nums">
                        {proc.pid}
                        {others > 0 && ` · +${others} more`}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>{row.httpCount && !row.flowCount ? "TLS proxy" : "host capture"}</div>
                      <div className="faint">no pid</div>
                    </>
                  )}
                </td>
                <td className="min-w-[20ch] whitespace-normal py-2 align-top">
                  <div className={row.requests.length ? "primary" : undefined} translate="no">
                    {req1}
                  </div>
                  <div className="faint">{req2}</div>
                </td>
                <td className="r py-2 align-top">
                  <div className="updown">
                    <span>↑ {row.sent ? bytes(row.sent) : "—"}</span>
                    <span>↓ {row.received ? bytes(row.received) : "—"}</span>
                  </div>
                </td>
                <td className="r py-2 align-top tabular-nums">{row.contacts.length ? rel(row.firstT) : "—"}</td>
                <td className="py-2 align-top">
                  <Rug contacts={row.contacts} axis={axis} now={now} background={row.background} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
